import React, { useEffect, useMemo, useState } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { ChevronUp, ExternalLink } from 'lucide-react';
import axios from 'axios';
import { Helmet } from 'react-helmet-async';
import generatedDreamPosts from './posts/pages';
import {
    ArticleHeader,
    BlogContainer,
    BlogTitle,
    BottomCta,
    NumberBadge,
    NumberBadgeList,
    PostContent,
    PostDate,
    RelatedCard,
    RelatedGrid,
    RelatedPostsSection,
    ScrollTopButton,
    SummaryBox,
    TagList,
    TagPill,
    TocBox
} from './BlogStyles';

const generatedPostMap = new Map(generatedDreamPosts.map((post) => [post.slug, post]));

const mapApiPost = (rawPost) => {
    if (rawPost.TITLE && rawPost.CONTENT) {
        return {
            id: rawPost.ID,
            title: rawPost.TITLE,
            content: rawPost.CONTENT,
            excerpt: rawPost.EXCERPT,
            slug: rawPost.SLUG,
            createdAt: rawPost.CREATED_AT,
            updatedAt: rawPost.UPDATED_AT,
            category: rawPost.CATEGORY || '꿈해몽',
            tags: rawPost.TAGS || ['꿈해몽'],
            seo: {
                title: rawPost.SEO_TITLE,
                description: rawPost.SEO_DESCRIPTION,
                canonicalUrl: rawPost.SEO_CANONICAL_URL
            }
        };
    }

    return rawPost;
};

const formatDate = (dateString) => {
    if (!dateString) return '';

    try {
        const date = new Date(dateString);
        if (Number.isNaN(date.getTime())) {
            return '';
        }

        return date.toLocaleDateString('ko-KR', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    } catch (error) {
        return '';
    }
};

const getSeoData = (post) => ({
    title: post.seo?.title || post.title || '몽픽 블로그',
    description: post.seo?.description || post.excerpt || '꿈해몽의 새로운 패러다임',
    canonicalUrl: post.seo?.canonicalUrl || post.canonicalUrl || `https://mongpick.com/blog/${post.slug}`,
    ogTitle: post.seo?.ogTitle || post.seo?.title || post.title || '몽픽 블로그',
    ogDescription: post.seo?.ogDescription || post.seo?.description || post.excerpt || '꿈해몽의 새로운 패러다임'
});

const getRelatedPosts = (post) => {
    if (!post?.slug) return [];
    const tagSet = new Set(post.tags || []);

    return generatedDreamPosts
        .filter((candidate) => candidate.slug !== post.slug)
        .map((candidate) => {
            const tagScore = (candidate.tags || []).filter((tag) => tagSet.has(tag)).length;
            const categoryScore = candidate.category === post.category ? 2 : 0;
            return { ...candidate, score: tagScore + categoryScore };
        })
        .filter((candidate) => candidate.score > 0)
        .sort((a, b) => b.score - a.score || new Date(b.publishedAt) - new Date(a.publishedAt))
        .slice(0, 6);
};

const BlogPost = () => {
    const { slug } = useParams();
    const navigate = useNavigate();
    const [post, setPost] = useState(null);
    const [loading, setLoading] = useState(true);
    const [showScroll, setShowScroll] = useState(false);

    useEffect(() => {
        const fetchPost = async () => {
            const localPost = generatedPostMap.get(slug);
            if (localPost) {
                setPost(localPost);
                setLoading(false);
                return;
            }

            try {
                const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/dream/post/${slug}`);
                setPost(mapApiPost(response.data));
            } catch (error) {
                console.error('Failed to fetch post:', error);
                navigate('/blog');
            } finally {
                setLoading(false);
            }
        };

        if (slug) {
            fetchPost();
        }
    }, [slug, navigate]);

    useEffect(() => {
        const checkScrollTop = () => {
            setShowScroll(window.pageYOffset > 400);
        };

        window.addEventListener('scroll', checkScrollTop);
        return () => window.removeEventListener('scroll', checkScrollTop);
    }, []);

    const relatedPosts = useMemo(() => getRelatedPosts(post), [post]);

    const scrollToTop = () => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    if (loading) return <div>Loading...</div>;
    if (!post) return <Navigate to="/blog" replace />;

    const seoData = getSeoData(post);
    const displayDate = formatDate(post.publishedAt || post.createdAt || post.updatedAt);
    const isLottoGuide = post.category === '로또 번호 만들기' || post.tags?.includes('로또 번호 만들기');

    return (
        <>
            <Helmet>
                <title>{seoData.title}</title>
                <meta name="description" content={seoData.description} />
                <link rel="canonical" href={seoData.canonicalUrl} />
                <meta property="og:title" content={seoData.ogTitle} />
                <meta property="og:description" content={seoData.ogDescription} />
                <meta property="og:type" content="article" />
                <meta property="og:url" content={seoData.canonicalUrl} />
                {post.publishedAt && <meta property="article:published_time" content={post.publishedAt} />}
                {post.updatedAt && <meta property="article:modified_time" content={post.updatedAt} />}
                {post.category && <meta property="article:section" content={post.category} />}
                {post.tags?.map((tag) => (
                    <meta key={tag} property="article:tag" content={tag} />
                ))}
                {post.jsonLd && (
                    <script type="application/ld+json">{JSON.stringify(post.jsonLd)}</script>
                )}
                {post.faqLd && (
                    <script type="application/ld+json">{JSON.stringify(post.faqLd)}</script>
                )}
            </Helmet>

            <BlogTitle onClick={() => navigate('/blog')}>몽픽 꿈해몽 블로그</BlogTitle>
            <BlogContainer>
                <ArticleHeader>
                    <TagList>
                        {[post.category, ...(post.tags || [])].filter(Boolean).slice(0, 5).map((tag) => (
                            <TagPill key={tag}>{tag}</TagPill>
                        ))}
                    </TagList>
                    <h1>{post.title || '제목 없음'}</h1>
                    {displayDate && <PostDate>{displayDate}</PostDate>}
                    {post.recommendedNumbers?.length ? (
                        <NumberBadgeList aria-label="추천 번호">
                            {post.recommendedNumbers.map((number) => (
                                <NumberBadge key={number}>{number}</NumberBadge>
                            ))}
                        </NumberBadgeList>
                    ) : null}
                    {post.excerpt && <SummaryBox>{post.excerpt}</SummaryBox>}
                </ArticleHeader>

                {post.toc?.length ? (
                    <TocBox aria-label="글 목차">
                        <strong>목차</strong>
                        <div>
                            {post.toc.map((item) => (
                                <a key={item.id} href={`#${item.id}`}>{item.label}</a>
                            ))}
                        </div>
                    </TocBox>
                ) : null}

                <PostContent dangerouslySetInnerHTML={{ __html: post.content || '<p>내용이 없습니다.</p>' }} />

                {relatedPosts.length ? (
                    <RelatedPostsSection>
                        <h2>함께 보면 좋은 글</h2>
                        <RelatedGrid>
                            {relatedPosts.map((relatedPost) => (
                                <RelatedCard key={relatedPost.slug} to={`/blog/${relatedPost.slug}`}>
                                    <span>{relatedPost.category}</span>
                                    <strong>{relatedPost.title}</strong>
                                    <PostDate>{formatDate(relatedPost.publishedAt)}</PostDate>
                                </RelatedCard>
                            ))}
                        </RelatedGrid>
                    </RelatedPostsSection>
                ) : null}

                <BottomCta>
                    <p>
                        {isLottoGuide
                            ? '번호 고르는 기준을 정했다면, 몽픽 로또 번호 생성기로 한 번 더 가볍게 조합해보세요.'
                            : '꿈속 장면을 더 자세히 적으면 해석도 더 개인적인 흐름에 가까워집니다.'}
                    </p>
                    <a
                        href={isLottoGuide ? '/lotto' : 'https://mongpick.com/'}
                        aria-label={isLottoGuide ? '몽픽 로또 번호 생성기로 바로가기' : '몽픽에서 내 꿈 직접 해석하기'}
                    >
                        <ExternalLink size={18} />
                        {isLottoGuide ? '바로가기' : '몽픽에서 내 꿈 해석하기'}
                    </a>
                </BottomCta>

                <ScrollTopButton
                    onClick={scrollToTop}
                    $visible={showScroll}
                    aria-label="맨 위로 이동"
                >
                    <ChevronUp size={24} />
                </ScrollTopButton>
            </BlogContainer>
        </>
    );
};

export default BlogPost;
